// Controls navigation rail + flyout. The rail is a fixed vertical strip of
// icon-button tabs on the left edge (always visible). Clicking a tab opens
// the flyout to that section; clicking the active tab closes the flyout.
// Each top-level pane folder is tagged with `data-section` on its
// `.element` by its builder; the rail sets `data-active-section` on
// `#controls-container` (inside the flyout) and CSS rules show only the
// matching folder.
//
// `paneState.activeSection` is the currently-open section (undefined when
// the flyout is closed). `paneState.lastActiveSection` remembers the most
// recent open section so the comma hotkey can reopen to it.
//
// Each tab also carries a small "on/off" light:
//   - `data-on=N`  → accent dot, N primitives active in that section.
//   - `data-busy`  → amber pulse, async work in flight (Stage 4: trace +
//                    contour generation; Stage 5: criterion eval).
// Lights refresh on params changes and on the async-completion intents that
// signal scene-graph mutations (trace done → SET_FIELDLINE_DISPLAY; contour
// done → UI_TASK_HIDE).

import { paneState } from './state';
import { store, params } from '@store/instance';
import { INTENTS } from '@store/intents';
import type { Disposer } from '../types';
import type { ViewerContext } from '@app/context';

export type SectionKey =
    | 'selection'
    | 'slices'
    | 'contour'
    | 'volume'
    | 'fieldlines'
    | 'vectors'
    | 'particles'
    | 'reductions';

interface TabSpec {
    key: SectionKey;
    icon: string;
    label: string;
    /** When set, the tab is dimmed and click is suppressed. The string is
     *  appended to the hover tooltip so the user learns why. */
    disabled?: string;
}

// Workflow order: pick a region → render it (slices/contour/volume) → derive
// from it (field lines/vectors/particles) → extract scalars (reductions).
// Selections always renders in the rail (Stage 5 owns the registry; today it
// either drives the legacy cube section or a "coming in Stage 5" placeholder).
// Vectors / Particles ship disabled until webpic M5 / v0.2.
const TABS: TabSpec[] = [
    { key: 'selection',  icon: 'fa-vector-square',       label: 'Selections'        },
    { key: 'slices',     icon: 'fa-clone',               label: 'Field Slices'      },
    { key: 'contour',    icon: 'fa-mountain',            label: 'Contours'          },
    { key: 'volume',     icon: 'fa-cube',                label: 'Volume Rendering'  },
    { key: 'fieldlines', icon: 'fa-bezier-curve',        label: 'Field Lines'       },
    { key: 'vectors',    icon: 'fa-arrow-up',            label: 'Vectors',
      disabled: 'coming in webpic M5/v0.2' },
    { key: 'particles',  icon: 'fa-atom',                label: 'Particles',
      disabled: 'coming in webpic M5/v0.2' },
    { key: 'reductions', icon: 'fa-compress-arrows-alt', label: 'Reductions'        },
];

function activeCountFor(key: SectionKey, viewer: ViewerContext): number {
    switch (key) {
        case 'slices':
            return (params.showFieldSlice   ? 1 : 0)
                 + (params.showFieldSliceZY ? 1 : 0)
                 + (params.showFieldSliceZX ? 1 : 0);
        case 'fieldlines':
            return (params.showFieldLines && viewer.objects.fieldlines.lines.length > 0)
                ? viewer.objects.fieldlines.lines.length
                : 0;
        case 'volume':
            return params.volumeEnabled ? 1 : 0;
        case 'contour':
            return viewer.objects.contours.meshes.length;
        case 'selection':
            return params.selectionOrder.length;
        case 'reductions':
            // The Full-domain reduction is always seeded; light the tab only for
            // deliberate, selection-scoped extractions the user set up.
            return params.reductionOrder.reduce((n, id) => {
                const r = params.reductions[id];
                return n + (r && r.domain !== 'full' ? 1 : 0);
            }, 0);
        case 'vectors':
        case 'particles':
            return 0;
    }
}

// `includeSelection` is retained for back-compat with callers that may still
// pass it; it has no effect now — the Selections tab always renders.
export function initControlsTabs(opts: { includeSelection?: boolean; viewer: ViewerContext }): Disposer {
    void opts.includeSelection;
    const rail      = document.getElementById('controls-rail');
    const flyout    = document.getElementById('controls-flyout');
    const title     = document.getElementById('controls-flyout-title');
    const container = document.getElementById('controls-container');
    if (!rail || !flyout || !title || !container) return () => { /* rail not present */ };

    const offs: Disposer[] = [];

    const tabs = TABS;
    rail.innerHTML = tabs.map((t) => {
        const ariaLabel = t.disabled ? `${t.label} — ${t.disabled}` : t.label;
        const disabledAttr = t.disabled ? ' data-disabled="true" aria-disabled="true"' : '';
        return `<button class="controls-tab" type="button" role="tab" `
            + `data-section="${t.key}" aria-selected="false"`
            + `${disabledAttr} `
            + `aria-label="${ariaLabel}">`
            + `<i class="fas ${t.icon}" aria-hidden="true"></i>`
            + `<span class="controls-tab-light" aria-hidden="true"></span>`
            + `</button>`;
    }).join('');

    const refreshLights = (): void => {
        rail.querySelectorAll<HTMLButtonElement>('.controls-tab').forEach((b) => {
            const key = b.dataset['section'] as SectionKey | undefined;
            if (!key) return;
            const n = activeCountFor(key, opts.viewer);
            if (n > 0) b.dataset['on'] = String(n);
            else delete b.dataset['on'];
            if (paneState.busy.has(key)) b.dataset['busy'] = 'true';
            else delete b.dataset['busy'];
        });
    };

    const labelOf = (key: SectionKey): string => tabs.find((t) => t.key === key)?.label ?? '';
    const isEnabled = (key: SectionKey): boolean =>
        !!tabs.find((t) => t.key === key && !t.disabled);

    // Position the flyout vertically: center on the active rail tab, then
    // clamp top to the rail's top (panel never floats above the rail) and
    // clamp the bottom to the viewport so the panel never spills off-screen.
    // The arrow's --arrow-pos tracks the tab's center within the (possibly
    // clamped) flyout, so the tail always lands on the originating button.
    const VIEWPORT_MARGIN = 20;
    const syncFlyoutPosition = (btn: HTMLButtonElement | null): void => {
        if (!btn) return;
        const tabRect      = btn.getBoundingClientRect();
        const railRect     = rail.getBoundingClientRect();
        // Read height after the flyout is visible so the rect is valid.
        const flyoutHeight = flyout.getBoundingClientRect().height;
        const viewportH    = window.innerHeight;

        const tabCenter   = tabRect.top + tabRect.height / 2;
        const desiredTop  = tabCenter - flyoutHeight / 2;
        const minTop      = railRect.top;
        const maxTop      = viewportH - flyoutHeight - VIEWPORT_MARGIN;
        const clampedTop  = Math.max(minTop, Math.min(desiredTop, maxTop));

        flyout.style.top = `${clampedTop}px`;
        flyout.style.setProperty('--arrow-pos', `${tabCenter - clampedTop}px`);
    };

    const setOpen = (key: SectionKey | null): void => {
        const prev = (paneState.activeSection as SectionKey | undefined) ?? null;
        if (key) {
            paneState.activeSection = key;
            paneState.lastActiveSection = key;
            container.dataset['activeSection'] = key;
            title.textContent = labelOf(key);
            flyout.classList.remove('is-hidden');
        } else {
            delete paneState.activeSection;
            delete container.dataset['activeSection'];
            flyout.classList.add('is-hidden');
        }
        let activeBtn: HTMLButtonElement | null = null;
        rail.querySelectorAll<HTMLButtonElement>('.controls-tab').forEach((b) => {
            const isActive = !!key && b.dataset['section'] === key;
            b.setAttribute('aria-selected', isActive ? 'true' : 'false');
            if (isActive) activeBtn = b;
        });
        if (key && activeBtn) syncFlyoutPosition(activeBtn);
        // Notify subscribers (e.g. the contour live-preview lifecycle) of the
        // section change. Only on an actual change so no-op opens (initial
        // restore, re-clicking nothing) don't fan out.
        const next = key ?? null;
        if (next !== prev) store.dispatch(INTENTS.UI_TAB_CHANGED, { which: next });
    };

    // Recompute on viewport resize — the rail is vertically centered, so its
    // top moves with window height, which can shift the clamp floor and the
    // tab's y. Cheap, no rAF needed.
    const onResize = (): void => {
        const k = paneState.activeSection as SectionKey | undefined;
        if (!k) return;
        const btn = rail.querySelector<HTMLButtonElement>(`.controls-tab[data-section="${k}"]`);
        syncFlyoutPosition(btn);
    };
    window.addEventListener('resize', onResize);
    offs.push(() => window.removeEventListener('resize', onResize));

    // Default: closed. paneState.activeSection survives pane rebuilds
    // (dataset switch) — honour it so the flyout stays in sync. On a fresh
    // page load activeSection is undefined, so the flyout starts hidden.
    const initial = paneState.activeSection as SectionKey | undefined;
    setOpen(initial && isEnabled(initial) ? initial : null);
    refreshLights();

    const onRailClick = (e: MouseEvent): void => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.controls-tab');
        if (!btn) return;
        if (btn.dataset['disabled'] === 'true') return;
        const key = btn.dataset['section'] as SectionKey | undefined;
        if (!key) return;
        setOpen(paneState.activeSection === key ? null : key);
    };
    rail.addEventListener('click', onRailClick);
    offs.push(() => rail.removeEventListener('click', onRailClick));

    const closeBtn = document.getElementById('controls-flyout-close');
    if (closeBtn) {
        const onCloseClick = (): void => setOpen(null);
        closeBtn.addEventListener('click', onCloseClick);
        offs.push(() => closeBtn.removeEventListener('click', onCloseClick));
    }

    // Comma key dispatches UI_PANEL_TOGGLE { which: 'controls' }. The old
    // panels.ts handler walked #controls-panel; we own the behaviour now.
    offs.push(store.onIntent(INTENTS.UI_PANEL_TOGGLE, (p) => {
        if (!p || (p as { which?: string }).which !== 'controls') return;
        if (paneState.activeSection) { setOpen(null); return; }
        const fallbackKey = (paneState.lastActiveSection as SectionKey | undefined) ?? 'slices';
        const target = isEnabled(fallbackKey) ? fallbackKey : (isEnabled('slices') ? 'slices' : tabs[0]?.key);
        if (target) setOpen(target);
    }));

    // Stage 7 — palette / programmatic tab open. Distinct from UI_PANEL_TOGGLE
    // because the palette needs to jump to a *specific* section regardless of
    // current open state. Disabled sections still no-op so the palette entry
    // can stay listed without surprising the user.
    offs.push(store.onIntent(INTENTS.UI_TAB_OPEN, (p) => {
        if (!p) return;
        const key = p.which as SectionKey;
        if (!isEnabled(key)) return;
        setOpen(key);
    }));

    // Update lights on state changes. params subscribe covers slice / volume /
    // selection toggles + fieldline visibility. UI_PANE_REFRESH fires after a
    // trace completes (via SET_FIELDLINE_DISPLAY → applyDisplay). UI_TASK_HIDE
    // fires when contour generation finishes its async marching-cubes pass.
    // CLEAR_* intents are synchronous so we react immediately.
    offs.push(store.subscribe('params', refreshLights));
    offs.push(store.onIntent(INTENTS.UI_PANE_REFRESH,    refreshLights));
    offs.push(store.onIntent(INTENTS.UI_TASK_HIDE,       refreshLights));
    offs.push(store.onIntent(INTENTS.CLEAR_FIELDLINES,   refreshLights));
    offs.push(store.onIntent(INTENTS.CLEAR_CONTOURS,     refreshLights));

    // Busy-state intent wiring. Trace + contour generation are async; the
    // amber pulse lets the user see the work is in flight without watching
    // the global #task-indicator. Completion intents (SET_FIELDLINE_DISPLAY
    // for traces, UI_TASK_HIDE for contour) are also subscribed above for
    // light refresh, so a single refreshLights() picks up the cleared state.
    const setBusy = (key: SectionKey, on: boolean): void => {
        if (on) paneState.busy.add(key);
        else    paneState.busy.delete(key);
        refreshLights();
    };
    offs.push(store.onIntent(INTENTS.TRACE_FIELDLINE,       () => setBusy('fieldlines', true)));
    offs.push(store.onIntent(INTENTS.SET_FIELDLINE_DISPLAY, () => setBusy('fieldlines', false)));
    offs.push(store.onIntent(INTENTS.GENERATE_CONTOUR,      () => setBusy('contour', true)));
    offs.push(store.onIntent(INTENTS.UI_TASK_HIDE,          () => setBusy('contour', false)));
    offs.push(store.onIntent(INTENTS.CLEAR_FIELDLINES,      () => setBusy('fieldlines', false)));
    offs.push(store.onIntent(INTENTS.CLEAR_CONTOURS,        () => setBusy('contour', false)));

    return () => { for (const off of offs) { try { off(); } catch { /* swallow */ } } };
}
